import { createContext, type Dispatch, type ReactNode, useEffect, useMemo, useReducer } from "react";
import { uniqBy } from "lodash";

import { Attribute, QuickRankMode } from "../../gql/graphql";
import {
  type QuickRankAction,
  QuickRankActionName,
  type QuickRankGameConfiguration,
  type QuickRankGameResults,
  QuickRankNonRenderableDestinations,
  quickRankReducer,
  QuickRankRenderableDestinations,
  type QuickRankState,
} from "../../reducers/quickrank";
import { shuffleArray } from "../../utils";

export const QuickRankStateContext = createContext<[QuickRankState, Dispatch<QuickRankAction>] | null>(null);

const { DECK, HAND, RATED } = QuickRankRenderableDestinations;
const { ALL, TO_RATE } = QuickRankNonRenderableDestinations;

interface QuickRankStateProviderProps {
  children: ReactNode;
  configuration: QuickRankGameConfiguration;
  cardsPerHand: number;
  onGameFinished: (results: QuickRankGameResults, startTime: Date) => Promise<void>;
}

type CardWithIndex = { index: number; attribute: Attribute };

const twoPickNCards = (attributes: Attribute[]) => {
  const attributePairs = attributes
    .map((attribute, index) => {
      const result = [];
      for (let i = index + 1; i < attributes.length; i++) {
        result.push(shuffleArray([attribute, attributes[i]]));
      }
      return result;
    })
    .flat();

  const shuffledAttributePairs = shuffleArray(attributePairs).flat();

  return shuffledAttributePairs.map((attribute, index) => ({
    index,
    attribute,
  }));
};

const allUpCards = (attributes: Attribute[], isShuffled: boolean): CardWithIndex[] => {
  const mappedAttributes = attributes.map((attribute, index) => ({
    index,
    attribute,
  }));

  return isShuffled ? shuffleArray(mappedAttributes) : mappedAttributes;
};

const cardsCombinators = {
  [QuickRankMode.TwoPickN]: twoPickNCards,
  [QuickRankMode.AllUp]: allUpCards,
  [QuickRankMode.Optimized]: allUpCards,
};

export const QuickRankStateProvider = (props: QuickRankStateProviderProps) => {
  const { children, configuration, cardsPerHand, onGameFinished } = props;
  const { attributes, mode } = configuration;
  const isOptimizedMode = mode === QuickRankMode.Optimized;
  const cards = useMemo(() => cardsCombinators[mode](attributes, configuration.shuffleCards), [attributes, mode]);

  const [state, dispatch] = useReducer(quickRankReducer, {
    cards: {
      [ALL]: [...cards],
      [DECK]: [...cards],
      [HAND]: new Array(cardsPerHand).fill(null),
      [RATED]: [],
      [TO_RATE]: configuration.showRatingScale ? [...uniqBy(cards, "attribute")] : [],
    },
    rating: [],
    status: {
      round: 0,
      // isPreviewModalOpen: localStorage.getItem("dontShowPreviewModal") !== "true",
      isPreviewModalOpen: false,
      shouldChangeRound: true,
      isGameFinished: false,
      isRatingRejectedCards: false,
      startTime: new Date(),
      displayCurrentIndex: null,
      optimizedModeMatrix: isOptimizedMode
        ? {
            currentLayer: cards.map((card) => [card]),
            nextLayer: Array.from({ length: Math.ceil(cards.length / 2) }, () => []),
            checkedIndexes: [0, 1],
          }
        : null,
    },
    results: attributes.reduce(
      (acc, { id }) => ({
        ...acc,
        [id]: {
          count: 0,
          rank: 0,
          rating: 0,
        },
      }),
      {},
    ),
    configuration,
  });

  useEffect(() => {
    const { shouldChangeRound, isPreviewModalOpen, round } = state.status;
    if (isPreviewModalOpen) {
      return;
    }

    if (shouldChangeRound && round === 0) {
      dispatch({ type: QuickRankActionName.NEXT_ROUND });
    } else {
      setTimeout(() => dispatch({ type: QuickRankActionName.NEXT_ROUND }), 400);
    }
  }, [state.status.shouldChangeRound, state.status.isPreviewModalOpen]);

  useEffect(() => {
    const { isGameFinished, startTime } = state.status;
    const { [TO_RATE]: toRate, [RATED]: rated } = state.cards;
    const { mode: gameMode } = state.configuration;

    if (!isGameFinished || rated.length || toRate.length) {
      return;
    }

    let results = { ...state.results };

    if (gameMode === QuickRankMode.AllUp) {
      results = Object.keys(results).reduce((acc, key) => {
        const { count } = acc[key];
        return {
          ...acc,
          [key]: {
            ...acc[key],
            count,
            rank: count === 1 ? 1 : 2,
          },
        };
      }, results);
    }

    if (gameMode === QuickRankMode.TwoPickN) {
      const resultObjects = Object.keys(results).map((key) => ({
        ...results[key],
        id: key,
      }));

      const sortedResults = resultObjects.sort((a, b) => b.count - a.count);

      results = sortedResults.reduce((acc, { id }, index) => {
        return {
          ...acc,
          [id]: {
            ...acc[id],
            rank: index + 1,
          },
        };
      }, results);
    }

    onGameFinished(results, startTime);
  }, [state.status.isGameFinished, state.cards[TO_RATE].length, state.cards[RATED].length, state.results]);

  return <QuickRankStateContext.Provider value={[state, dispatch]}>{children}</QuickRankStateContext.Provider>;
};
